/**
 * Admin-only UI switches.
 *
 * These turn admin tooling on and off inside the app. They are a VIEW
 * preference, never a permission: every gated feature is already restricted to
 * the admin email on the server, so flipping one of these on in a normal user's
 * browser gets them nothing (the route still rejects the request). Keep it that
 * way — do not use these flags to unlock anything the server doesn't re-check.
 *
 * Stored in localStorage, so the choice is per browser and needs no migration.
 * Changes broadcast on a custom event so a page that is already mounted picks
 * the new value up immediately instead of waiting for a remount.
 */

import { useEffect, useState } from 'react';

const STORAGE_PREFIX = 'mars.adminSettings.';
const CHANGE_EVENT = 'mars:admin-settings-changed';

/**
 * Every switch the Admin Settings page renders. Add here and the page grows a
 * row for it — nothing else to edit.
 *
 * `defaultValue: false` means hidden until the admin turns it on.
 */
export const ADMIN_SETTINGS = [
  {
    key: 'csvUpload',
    label: 'Referring-domains CSV upload',
    description:
      'Show the CSV picker on Affiliate Gap and Competitor Spy. With a file uploaded, the run uses those referring domains instead of calling SEMrush.',
    appliesTo: 'Affiliate Gap · Competitor Spy',
    defaultValue: false
  }
];

const settingByKey = (key) => ADMIN_SETTINGS.find((setting) => setting.key === key);

const defaultFor = (key) => settingByKey(key)?.defaultValue ?? false;

/** Reads a switch. Falls back to its default if storage is unavailable. */
export const getAdminSetting = (key) => {
  try {
    const stored = window.localStorage.getItem(`${STORAGE_PREFIX}${key}`);
    if (stored === null) return defaultFor(key);
    return stored === 'true';
  } catch (error) {
    // Private mode / storage disabled — behave as if nothing was ever set.
    return defaultFor(key);
  }
};

/** Writes a switch and tells every listener in this tab about it. */
export const setAdminSetting = (key, value) => {
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${key}`, value ? 'true' : 'false');
  } catch (error) {
    console.warn('Could not persist admin setting:', key, error);
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { key, value } }));
};

/**
 * Subscribe to a switch. `listener` is called with the new boolean.
 * Returns the unsubscribe function.
 *
 * Listens to `storage` as well so flipping the switch in one tab updates the
 * others — `storage` only fires in tabs that did not make the change, which is
 * exactly why the custom event above exists for the tab that did.
 */
export const subscribeToAdminSetting = (key, listener) => {
  const onCustom = (event) => {
    if (event.detail?.key === key) listener(event.detail.value);
  };
  const onStorage = (event) => {
    if (event.key === `${STORAGE_PREFIX}${key}`) listener(getAdminSetting(key));
  };

  window.addEventListener(CHANGE_EVENT, onCustom);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onCustom);
    window.removeEventListener('storage', onStorage);
  };
};

/**
 * Read a switch and re-render when it changes.
 *
 * Live rather than read-once, so turning a switch off on the Admin Settings
 * page hides the feature straight away — including in another open tab.
 */
export const useAdminSetting = (key) => {
  const [value, setValue] = useState(() => getAdminSetting(key));

  useEffect(() => {
    setValue(getAdminSetting(key));
    return subscribeToAdminSetting(key, setValue);
  }, [key]);

  return value;
};
